// @ts-strict-ignore
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { importBudget } from 'loot-core/src/client/actions/budgets';

import { styles, theme } from '../../style';
import { Block } from '../common/Block';
import { ButtonWithLoading } from '../common/Button';
import { Link } from '../common/Link';
import { Modal, type ModalProps } from '../common/Modal';
import { Paragraph } from '../common/Paragraph';
import { View } from '../common/View';
import { useResponsive } from '../../ResponsiveProvider';

function getErrorMessage(error: string): string {
  switch (error) {
    case 'parse-error':
      return 'Unable to parse file. Please select a JSON file exported from nYNAB.';
    case 'not-ynab5':
      return 'This file is not valid. Please select a JSON file exported from nYNAB.';
    default:
      return 'An unknown error occurred while importing. Please contact support to resolve this issue.';
  }
}

type ImportProps = {
  modalProps?: ModalProps;
};

export function ImportYNAB5({ modalProps }: ImportProps) {
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  async function onImport() {
    const res = await window.Actual?.openFileDialog({
      properties: ['openFile'],
      filters: [{ name: 'ynab', extensions: ['json'] }],
    });
    if (res) {
      setImporting(true);
      setError(null);
      try {
        await dispatch(importBudget(res[0], 'ynab5'));
      } catch (err) {
        setError(err.message);
      } finally {
        setImporting(false);
      }
    }
  }

  return (
    <Modal {...modalProps} title="Import from YNAB" style={{ width: 400 }}>
      {() => (
        <View
          style={{
            ...styles.smallText,
            lineHeight: 1.5,
            marginTop: 20,
          }}
        >
          {error && (
            <Block
              style={{
                color: theme.errorText,
                marginBottom: 15,
                textAlign: 'center',
              }}
            >
              {getErrorMessage(error)}
            </Block>
          )}

          <View
            style={{
              alignItems: 'center',
              '& > div': { lineHeight: '1.7em' },
              padding: 10,
            }}
          >
            <Paragraph>
              Please{' '}
              <Link
                variant="external"
                to="https://chatwoot.goodsteward.io/hc/gs-kb/articles/1790730359-m"
              >
                read here
              </Link>{' '}
              for detailed instructions on how to migrate your budget data from
              YNAB. You will need to follow these steps to export your data as a
              .JSON file before you can continue.
            </Paragraph>
            <Paragraph>
              If you have already exported your budget data, please click the
              button below to select the file to import to Good Steward. If
              there's any discrepancies, please contact{' '}
              <Link variant="external" to="mailto:support@goodsteward.io">
                support@goodsteward.io
              </Link>{' '}
            </Paragraph>
            <View>
              <ButtonWithLoading
                type="primary"
                loading={importing}
                onClick={onImport}
              >
                Select file...
              </ButtonWithLoading>
            </View>
          </View>
        </View>
      )}
    </Modal>
  );
}
